import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Panel } from '@cloudsam/ui';
import { fetchExplorerExchanges, fetchExplorerResults, fetchExplorerSessions, fetchSessionResponses, adaptSessionResponsesForResults, fetchSurveyStructure } from '../utils/explorerApi';
import MatrixView from './analysis/MatrixView';
import TimelineExchangeTable from './analysis/TimelineExchangeTable';
import { getFileUploads, getFileSignedUrl, getBulkFileUrls } from '../utils/cloudApi';
import { rowsToCsv, buildM3U, exportRowsCsv, buildFilename } from '../utils/exportUtils';
import {
    SESSION_COLUMNS, RESULT_COLUMNS, TIMELINE_COLUMNS,
    buildSessionRows, buildMatrixExport, buildResultRows, buildTimelineRows,
} from './analysis/sraExports';
import { lifecycleMessage } from '../utils/deploymentStats';
import { buildMatrixCells, mapNonAnswer, extractGcsPath } from '../utils/exploreData';
import { makeSortValue, sortResultRows, nextSortState, sortByGathered } from '../utils/resultsSort';
import { buildQuestionIdentity } from '../utils/questionIdentity';
import { tokens } from './listPanelStyles';
import AudioPlayChip from './AudioPlayChip';
import { showToast } from './Toast';
import {
    buildFolderName, planAudioClips, planUploads, pickDirectory, ensureSubdir,
    writeFileToDir, exportUploads, exportAudioClips, directoryPickerSupported,
} from '../utils/explorerExport';

function humanizeBytes(n) {
    if (!Number.isFinite(n)) return '';
    if (n < 1024) return `${n} bytes`;
    const kb = n / 1024;
    if (kb < 1024) return `${kb.toFixed(1)} KB`;
    return `${(kb / 1024).toFixed(1)} MB`;
}

const PAGE_SIZE_OPTIONS = [25, 50, 100];
// Explorer overlays share one stack: full-screen list panels sit above the
// workspace, and record-detail dialogs sit above those panels.
const MODAL_LAYERS = Object.freeze({ fullscreen: 1000, detail: 2000 });

// Confidence chip tiers (DEF-C10). Mirrors the prototype's level→class thresholds
// (level = round(conf*5); hi ≥4, md =3, lo else) and the matrix tint colors.
const CONF_TIER_STYLE = {
    hi: { background: '#E8F5E9', color: '#2E7D32' },
    md: { background: '#FFF9E6', color: '#7A6A3A' },
    lo: { background: '#FFEBEE', color: '#C62828' },
};

// Prototype segmented control (`.seg`). Shared by the top Sessions⇄Matrix toggle
// (DEF-C2) and the bottom Results⇄Timeline toggle (DEF-C8).
function Segmented({ options, value, onChange }) {
    return (
        <div style={styles.seg}>
            {options.map((o, i) => (
                <button
                    key={o.key}
                    type="button"
                    onClick={() => onChange(o.key)}
                    style={{
                        ...styles.segBtn,
                        ...(i < options.length - 1 ? styles.segBtnDivider : null),
                        ...(value === o.key ? styles.segBtnOn : null),
                    }}
                >{o.label}</button>
            ))}
        </div>
    );
}

function FullScreenListOverlay({ title, onClose, children, returnFocusRef, disableEscape = false }) {
    const closeRef = useRef(null);
    const onCloseRef = useRef(onClose);
    const disableEscapeRef = useRef(disableEscape);

    useEffect(() => { onCloseRef.current = onClose; }, [onClose]);
    useEffect(() => { disableEscapeRef.current = disableEscape; }, [disableEscape]);

    useEffect(() => {
        const returnFocusElement = returnFocusRef?.current;
        closeRef.current?.focus();
        const onKeyDown = (event) => {
            if (event.key === 'Escape' && !disableEscapeRef.current) {
                event.preventDefault();
                onCloseRef.current();
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return () => {
            window.removeEventListener('keydown', onKeyDown);
            returnFocusElement?.focus();
        };
    }, [returnFocusRef]);

    return (
        <div style={styles.fullscreenBackdrop} role="presentation">
            <section aria-modal="true" aria-label={`${title} full screen`} role="dialog" style={styles.fullscreenDialog}>
                <div style={styles.fullscreenHeader}>
                    <h2 style={styles.fullscreenTitle}>{title}</h2>
                    <button ref={closeRef} type="button" disabled={disableEscape} onClick={() => { if (!disableEscape) onClose(); }} style={styles.smallButton}>Close</button>
                </div>
                <div style={styles.fullscreenBody}>{children}</div>
            </section>
        </div>
    );
}

function useDebouncedValue(value, delayMs = 300) {
    const [debouncedValue, setDebouncedValue] = useState(value);
    useEffect(() => {
        const timer = window.setTimeout(() => setDebouncedValue(value), delayMs);
        return () => window.clearTimeout(timer);
    }, [value, delayMs]);
    return debouncedValue;
}

// Search the complete recorded payload, including nested answer structures,
// without assuming the data is JSON-safe (older result payloads can contain
// circularly-referenced objects in development fixtures).
function searchText(value, seen = new WeakSet()) {
    if (value == null) return '';
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint') return String(value);
    if (typeof value !== 'object') return '';
    if (seen.has(value)) return '';
    seen.add(value);
    if (Array.isArray(value)) return value.map(item => searchText(item, seen)).join(' ');
    return Object.values(value).map(item => searchText(item, seen)).join(' ');
}

function summarizeHuman(row) {
    try {
        const parts = Array.isArray(row?.human) ? row.human : [];
        const fallback = row?.raw_transcript || row?.human_text || row?.response_text || '';
        const text = (parts.length ? parts.join(' ') : fallback).replace(/\s+/g, ' ').trim();
        return text.length > 160 ? `${text.slice(0, 157)}...` : text;
    } catch (_) {
        return '';
    }
}

function latestAiMeta(row) {
    try {
        if (!Array.isArray(row?.ai_responses) || row.ai_responses.length === 0) return null;
        return row.ai_responses[row.ai_responses.length - 1] || null;
    } catch (_) {
        return null;
    }
}

function summarizeSam(row) {
    try {
        const ai = latestAiMeta(row);
        const text = ai?.text || row?.ai_response || row?.sam_response || row?.ai || row?.conversationalResponse || '';
        const cleaned = String(text).replace(/\s+/g, ' ').trim();
        return cleaned.length > 160 ? `${cleaned.slice(0, 157)}...` : cleaned;
    } catch (_) {
        return '';
    }
}

function buildPromptsAndNap(exchanges) {
    const prompts = {};
    const nap = {};
    for (const row of exchanges || []) {
        const seq = row.exchange_seq;
        if (seq == null) continue;
        if (row.prompts && Array.isArray(row.prompts)) prompts[seq] = row.prompts;
        for (const resp of (row.ai_responses || [])) {
            if (resp?.nextInstruction || resp?.shouldAdvanceSurvey != null || resp?.extractedData) {
                nap[seq] = resp;
            }
        }
    }
    return { prompts, nap };
}

// Synthesize a stable display handle for an anonymous take (no recipient),
// derived from the session's start timestamp: `anonymous<YYYYMMDDHHMMSS>`,
// e.g. anonymous20260707160327. Every row/card from one take shares it.
function synthesizeAnonymousHandle(session) {
    const ts = session?.started_at || session?.ts_start || session?.created_at;
    const d = ts ? new Date(ts) : null;
    if (!d || Number.isNaN(d.getTime())) return 'anonymous';
    const p = (n) => String(n).padStart(2, '0');
    return `anonymous${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}${p(d.getHours())}${p(d.getMinutes())}${p(d.getSeconds())}`;
}

function FileTag({ file }) {
    const [state, setState] = useState('idle');

    const handleDownload = useCallback(async () => {
        setState('loading');
        try {
            const url = await getFileSignedUrl(file.gcs_path, file.filename);
            window.open(url, '_blank');
            setState('idle');
        } catch (err) {
            console.error('[FileTag] download failed:', err);
            setState('error');
        }
    }, [file]);

    const sizeStr = file.file_size ? humanizeBytes(file.file_size) : '';
    return (
        <button type="button" onClick={handleDownload} title={`Download ${file.filename}`} style={{ ...styles.fileTag, opacity: state === 'loading' ? 0.6 : 1 }}>
            <span style={styles.fileName}>{file.filename}</span>
            {sizeStr && <span style={{ color: '#9ca3af' }}>({sizeStr})</span>}
            {state === 'error' && <span style={{ color: '#F44336' }}> error</span>}
        </button>
    );
}

function GcsFileTag({ gcsPath, filename }) {
    const [state, setState] = useState('idle');

    const handleDownload = useCallback(async (e) => {
        e.preventDefault();
        e.stopPropagation();
        setState('loading');
        try {
            const signedUrl = await getFileSignedUrl(gcsPath, filename);
            const a = document.createElement('a');
            a.href = signedUrl;
            a.download = filename || 'download';
            a.style.display = 'none';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            setState('idle');
        } catch (err) {
            console.error('[GcsFileTag] download failed:', err);
            setState('error');
            setTimeout(() => setState('idle'), 3000);
        }
    }, [gcsPath, filename]);

    return (
        <button type="button" onClick={handleDownload} disabled={state === 'loading'} title={`Download ${filename}`} style={styles.filechip}>
            {state === 'error' ? 'error' : filename}
        </button>
    );
}

const RESULTS_COLUMNS = ['Variable', 'Question', 'Value', 'Conf.', 'Voice', 'Upload'];

// Render responses.confidence (0..1, already populated by the kernel) as a tinted
// chip (DEF-C10): green high / amber mid / red low, thresholds and colors per the
// prototype. Dash when the row carries no confidence. Threaded through from
// fetchExplorerResults / adaptSessionResponsesForResults, which both keep the
// `confidence` field.
function renderConfidence(r) {
    const c = r?.confidence;
    if (c == null || c === '') return '—';
    const num = Number(c);
    if (!Number.isFinite(num)) return '—';
    const lvl = Math.max(1, Math.min(5, Math.round(num * 5)));
    const tier = lvl >= 4 ? 'hi' : lvl === 3 ? 'md' : 'lo';
    return <span style={{ ...styles.confChip, ...CONF_TIER_STYLE[tier] }}>{Math.round(num * 100)}%</span>;
}

// Two small arrows beside a heading. Both are always drawn so the column reads
// as sortable before it has been clicked; the active direction is the solid
// one. Decorative — the button that owns them carries the label and title.
function SortArrows({ direction }) {
    return (
        <span aria-hidden="true" style={styles.sortArrows}>
            <span style={{ ...styles.sortArrow, opacity: direction === 'asc' ? 1 : 0.3 }}>▲</span>
            <span style={{ ...styles.sortArrow, opacity: direction === 'desc' ? 1 : 0.3 }}>▼</span>
        </span>
    );
}

function RecipientResultsTable({ rows, sessionId, textByKey = {} }) {
    const [fileMeta, setFileMeta] = useState({});
    // Author-adjustable column widths (§1.3). Not persisted — a plain array of
    // pixel widths (undefined = auto), reset to default on every mount/refresh.
    const [colWidths, setColWidths] = useState(() => RESULTS_COLUMNS.map(() => undefined));
    // null means the gathered order — the default, and what a third click
    // returns to. Otherwise { key: column label, dir: 'asc' | 'desc' }.
    const [sort, setSort] = useState(null);
    const thRefs = useRef([]);
    const dragRef = useRef(null);

    useEffect(() => {
        if (!sessionId) return;
        let cancelled = false;
        getFileUploads(sessionId).then(files => {
            if (cancelled || !Array.isArray(files)) return;
            const map = {};
            for (const f of files) {
                if (f.gcs_path) map[f.gcs_path] = f;
            }
            setFileMeta(map);
        }).catch(() => { });
        return () => { cancelled = true; };
    }, [sessionId]);

    // Column-resize drag: capture the header's current rendered width on
    // mousedown, then follow the pointer. State-only, so a refresh resets it.
    const startResize = useCallback((index, e) => {
        e.preventDefault();
        e.stopPropagation();
        const th = thRefs.current[index];
        const startWidth = colWidths[index] != null
            ? colWidths[index]
            : (th ? th.getBoundingClientRect().width : 120);
        dragRef.current = { index, startX: e.clientX, startWidth };
        const handleMove = (ev) => {
            if (!dragRef.current) return;
            const { index: i, startX, startWidth: sw } = dragRef.current;
            const next = Math.max(40, sw + (ev.clientX - startX));
            setColWidths(prev => {
                const copy = prev.slice();
                copy[i] = next;
                return copy;
            });
        };
        const handleUp = () => {
            dragRef.current = null;
            window.removeEventListener('mousemove', handleMove);
            window.removeEventListener('mouseup', handleUp);
        };
        window.addEventListener('mousemove', handleMove);
        window.addEventListener('mouseup', handleUp);
    }, [colWidths]);

    // Ordering lives in utils/resultsSort so it can be tested without a DOM.
    const sortValue = useMemo(() => makeSortValue({ fileMeta, textByKey }), [fileMeta, textByKey]);
    const sortedRows = useMemo(() => sortResultRows(rows, sort, sortValue), [rows, sort, sortValue]);
    const toggleSort = useCallback((key) => setSort(prev => nextSortState(prev, key)), []);

    function renderValue(val) {
        if (val == null) return '';
        const gcsPath = extractGcsPath(val);
        if (gcsPath) {
            const meta = fileMeta[gcsPath];
            const filename = meta?.filename || decodeURIComponent(gcsPath.split('/').pop() || gcsPath);
            const sizeStr = meta?.file_size != null ? humanizeBytes(meta.file_size) : '';
            return `${filename}${sizeStr ? ` - ${sizeStr}` : ''}`;
        }
        if (typeof val === 'string') return mapNonAnswer(val);
        if (typeof val === 'number' || typeof val === 'boolean') return String(val);
        return JSON.stringify(val);
    }

    // Recording is a response artifact, not a field: it appears as a compact
    // play arrow only when this answer has captured audio.
    function renderVoice(r) {
        if (!r.audio_storage_path) return '—';
        return <AudioPlayChip gcsPath={r.audio_storage_path} />;
    }

    // Upload cell (DEF-C11): the filename as a downloadable file chip (note.pdf).
    // Dash when there's none.
    function renderUpload(r) {
        const gcsPath = extractGcsPath(r.value_json);
        if (!gcsPath) return '—';
        const meta = fileMeta[gcsPath];
        const filename = meta?.filename || decodeURIComponent(gcsPath.split('/').pop() || 'file');
        return <GcsFileTag gcsPath={gcsPath} filename={filename} />;
    }

    return (
        <table style={styles.table}>
            <thead>
                <tr>
                    {RESULTS_COLUMNS.map((label, i) => {
                        const direction = sort && sort.key === label ? sort.dir : null;
                        const next = direction === 'asc' ? 'sort descending'
                            : direction === 'desc' ? 'return to the order answers were gathered'
                                : 'sort ascending';
                        return (
                        <th
                            key={label}
                            ref={el => { thRefs.current[i] = el; }}
                            aria-sort={direction === 'asc' ? 'ascending' : direction === 'desc' ? 'descending' : 'none'}
                            style={{ ...styles.th, ...styles.resultsTh, width: colWidths[i] != null ? colWidths[i] : undefined }}
                        >
                            {/* The label is a button rather than a click handler on
                                the whole cell: the resize grip lives in this same
                                cell, and a drag that ends a pixel inside the header
                                must not also re-sort the table. */}
                            <button
                                type="button"
                                onClick={() => toggleSort(label)}
                                title={`${label} — click to ${next}`}
                                style={styles.sortButton}
                            >
                                <span>{label}</span>
                                <SortArrows direction={direction} />
                            </button>
                            {i < RESULTS_COLUMNS.length - 1 && (
                                <span
                                    role="separator"
                                    aria-orientation="vertical"
                                    aria-label={`Resize ${label} column`}
                                    onMouseDown={(e) => startResize(i, e)}
                                    style={styles.colResizeHandle}
                                />
                            )}
                        </th>
                        );
                    })}
                </tr>
            </thead>
            <tbody>
                {sortedRows.map((r, idx) => {
                    const questionText = textByKey[String(r.variable_name)] ?? '';
                    return (
                        <tr key={`${r.id || r.variable_name || 'row'}-${idx}`}>
                            <td style={styles.td}>{String(r.variable_name || '')}</td>
                            <td style={{ ...styles.td, whiteSpace: 'normal', wordBreak: 'break-word' }}>{questionText || '-'}</td>
                            <td style={{ ...styles.td, fontFamily: styles.mono, whiteSpace: 'normal', wordBreak: 'break-word' }}>{renderValue(r.value_json)}</td>
                            <td style={{ ...styles.td, fontVariantNumeric: 'tabular-nums' }}>{renderConfidence(r)}</td>
                            <td style={styles.td}>{renderVoice(r)}</td>
                            <td style={styles.td}>{renderUpload(r)}</td>
                        </tr>
                    );
                })}
            </tbody>
        </table>
    );
}

function SessionFileUploads({ sessionId }) {
    const [files, setFiles] = useState(null);
    const [downloading, setDownloading] = useState(false);

    useEffect(() => {
        let cancelled = false;
        getFileUploads(sessionId).then(f => { if (!cancelled) setFiles(f); }).catch(() => { if (!cancelled) setFiles([]); });
        return () => { cancelled = true; };
    }, [sessionId]);

    const handleBulkDownload = useCallback(async () => {
        if (!files || files.length === 0) return;
        setDownloading(true);
        try {
            const urls = await getBulkFileUrls(files.map(f => f.gcs_path));
            for (const item of urls) window.open(item.url || item.signed_url, '_blank');
        } catch (err) {
            console.error('[SessionFileUploads] bulk download failed:', err);
        } finally {
            setDownloading(false);
        }
    }, [files]);

    if (files === null) return <span style={{ fontSize: 12, color: '#9ca3af' }}>loading files...</span>;
    if (files.length === 0) return null;

    return (
        <div style={styles.filesBox}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: '#3C4043' }}>Files ({files.length})</span>
                {files.length > 1 && <button onClick={handleBulkDownload} disabled={downloading} style={styles.smallButton}>{downloading ? '...' : 'Download All'}</button>}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {files.map((f, i) => <FileTag key={`${f.gcs_path || f.filename}-${i}`} file={f} />)}
            </div>
        </div>
    );
}

function PromptModal({ promptModal, setPromptModal }) {
    useEffect(() => {
        if (!promptModal) return undefined;
        const onKeyDown = (event) => {
            if (event.key === 'Escape') {
                event.preventDefault();
                setPromptModal(null);
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [promptModal, setPromptModal]);

    if (!promptModal) return null;
    return (
        <div style={styles.modalBackdrop} onClick={() => setPromptModal(null)}>
            <div role="dialog" aria-modal="true" aria-label={`Prompt details for exchange ${promptModal.exchange_seq}`} style={styles.modal} onClick={(e) => e.stopPropagation()}>
                <div style={styles.modalHeader}>
                    <div style={{ fontWeight: 600 }}>Prompt - Exchange {promptModal.exchange_seq}</div>
                    <button onClick={() => setPromptModal(null)}>Close</button>
                </div>
                <div style={{ fontSize: 12, color: '#5F6368', marginBottom: 8 }}>
                    <span style={{ marginRight: 12 }}>type: {promptModal.prompt.prompt_type || 'text'}</span>
                    {Number.isFinite(promptModal.prompt.bytes) && <span>bytes: {promptModal.prompt.bytes}</span>}
                </div>
                <div style={{ marginBottom: 8 }}>
                    <button onClick={() => navigator.clipboard?.writeText(String(promptModal.prompt.prompt_text || ''))}>Copy</button>
                    <button style={{ marginLeft: 8 }} onClick={() => setPromptModal({ ...promptModal, wrap: !promptModal.wrap })}>{promptModal.wrap ? 'No Wrap' : 'Wrap lines'}</button>
                </div>
                <pre style={{ ...styles.pre, whiteSpace: promptModal.wrap ? 'pre-wrap' : 'pre' }}>{String(promptModal.prompt.prompt_text || '').replace(/\\n/g, '\n')}</pre>
            </div>
        </div>
    );
}

function NapModal({ napModal, setNapModal }) {
    useEffect(() => {
        if (!napModal) return undefined;
        const onKeyDown = (event) => {
            if (event.key === 'Escape') {
                event.preventDefault();
                setNapModal(null);
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [napModal, setNapModal]);

    if (!napModal) return null;
    const nap = napModal.nap || {};
    const extracted = nap?.extractedData && typeof nap.extractedData === 'object' ? nap.extractedData : {};
    const analysis = nap?.analysis || {};
    const next = nap?.nextInstruction || {};
    const adv = nap?.shouldAdvanceSurvey;

    return (
        <div style={styles.modalBackdrop} onClick={() => setNapModal(null)}>
            <div role="dialog" aria-modal="true" aria-label={`NAP details for exchange ${napModal.exchange_seq}`} style={styles.modal} onClick={(e) => e.stopPropagation()}>
                <div style={styles.modalHeader}>
                    <div style={{ fontWeight: 600 }}>NAP - Exchange {napModal.exchange_seq}</div>
                    <button onClick={() => setNapModal(null)}>Close</button>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
                    <div>
                        <div style={{ fontWeight: 600, marginBottom: 4 }}>Extracted Data</div>
                        {Object.keys(extracted).length === 0 ? <div style={{ color: '#5F6368' }}>&lt;none&gt;</div> : (
                            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                                {Object.entries(extracted).map(([k, v]) => (
                                    <li key={k} style={{ padding: '2px 0' }}>
                                        <strong>{k}:</strong> <span style={{ fontFamily: styles.mono }}>{typeof v === 'string' ? v : JSON.stringify(v)}</span>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                    <div>
                        <div style={{ fontWeight: 600, marginBottom: 4 }}>Evaluation</div>
                        <div style={{ fontSize: 13 }}><strong>shouldAdvance:</strong> {adv === true ? 'true' : adv === false ? 'false' : '?'}</div>
                        <div style={{ fontSize: 13 }}><strong>next.action:</strong> {next.action || next.type || ''}</div>
                        {(next.message || next.content || next.text) && <div style={{ fontSize: 13 }}><strong>next.message:</strong> {next.message || next.content || next.text}</div>}
                        {analysis.confidence != null && <div style={{ fontSize: 13 }}><strong>confidence:</strong> {String(analysis.confidence)}</div>}
                    </div>
                </div>
                <button onClick={() => setNapModal(m => ({ ...m, showJson: !m.showJson }))}>{napModal.showJson ? 'Hide JSON' : 'View JSON'}</button>
                {napModal.showJson && <pre style={styles.pre}>{JSON.stringify(napModal.nap, null, 2)}</pre>}
            </div>
        </div>
    );
}

export default function ExploreTab({ deploymentId, surveyId, surveyName, deploymentResponses = [], refreshNonce = 0, exportHandlerRef, enrichedDeployment = null, onOpenInAnalysis, openSession = null, onSessionOpened }) {
    const [topView, setTopView] = useState('sessions'); // 'sessions' | 'matrix' (WP-C1)
    const [sessions, setSessions] = useState([]);
    const [sessionsAt, setSessionsAt] = useState(null);
    const [exchangesAt, setExchangesAt] = useState(null);
    const [sessionsError, setSessionsError] = useState('');
    const [resultsRows, setResultsRows] = useState([]);
    const [, setResultsAt] = useState(null); // fetch bookkeeping; timestamp no longer surfaced (DEF-C8)
    const [resultsLoading, setResultsLoading] = useState(false);
    const [resultsError, setResultsError] = useState('');
    // Session-keyed responses fallback for anonymous takes that have no
    // recipient-keyed `results` rows. Keyed by session_id → adapted rows.
    const [sessionResponsesBySessionId, setSessionResponsesBySessionId] = useState({});
    const [selectedSessionIds, setSelectedSessionIds] = useState(new Set());
    const [sessionSearch, setSessionSearch] = useState('');
    const [sessionsPage, setSessionsPage] = useState(0);
    const [sessionsPageSize, setSessionsPageSize] = useState(50);
    const [fullscreenPane, setFullscreenPane] = useState(null);
    const [splitRatio, setSplitRatio] = useState(0.45);
    const [dragging, setDragging] = useState(false);
    const [resultsMode, setResultsMode] = useState('timeline');
    const [timelineSearch, setTimelineSearch] = useState('');
    const [timelineSearchFocus, setTimelineSearchFocus] = useState(null);
    const [expandedSessionIds, setExpandedSessionIds] = useState(new Set());
    const [expandedResultRecipients, setExpandedResultRecipients] = useState(new Set());
    const [exchangesBySessionId, setExchangesBySessionId] = useState({});
    const [conversationSearchTextBySession, setConversationSearchTextBySession] = useState({});
    const [promptsByExchange, setPromptsByExchange] = useState({});
    const [napByExchange, setNapByExchange] = useState({});
    const [promptModal, setPromptModal] = useState(null);
    const [napModal, setNapModal] = useState(null);
    // Survey descriptor structure (questions[]), used to (a) bridge audio from
    // per-session responses onto named-recipient results rows via the
    // variable_name→question_id map, and (b) look up question text (§1.1, §1.2).
    const [surveyStructure, setSurveyStructure] = useState(null);
    // Per-session audio bridge: session_id → { variable_name → audio_storage_path }.
    const [sessionAudioBySessionId, setSessionAudioBySessionId] = useState({});
    const containerRef = useRef(null);
    const sessionsListRef = useRef(null);
    const resultsListRef = useRef(null);
    const sessionsListBodyRef = useRef(null);
    const resultsListBodyRef = useRef(null);
    const topFullscreenButtonRef = useRef(null);
    const bottomFullscreenButtonRef = useRef(null);
    const paneScrollPositionsRef = useRef({ top: 0, bottom: 0 });
    const conversationSearchCacheRef = useRef(new Map());

    useEffect(() => {
        let cancelled = false;
        setSelectedSessionIds(new Set());
        setExpandedSessionIds(new Set());
        setExpandedResultRecipients(new Set());
        setExchangesBySessionId({});
        setPromptsByExchange({});
        setNapByExchange({});
        setSessionResponsesBySessionId({});
        setSessionAudioBySessionId({});
        setSessionsError('');

        if (!deploymentId) {
            setSessions([]);
            setResultsRows([]);
            return () => { cancelled = true; };
        }

        fetchExplorerSessions(surveyId, deploymentId).then(rows => {
            if (cancelled) return;
            setSessions(Array.isArray(rows) ? rows : []);
            setSessionsAt(Date.now());
        }).catch(err => {
            if (!cancelled) {
                setSessions([]);
                setSessionsError(err?.message || 'Failed to load sessions');
            }
        });

        setResultsLoading(true);
        setResultsError('');
        fetchExplorerResults(deploymentId).then(rows => {
            if (cancelled) return;
            setResultsRows(Array.isArray(rows) ? rows : []);
            setResultsAt(Date.now());
        }).catch(err => {
            if (!cancelled) setResultsError(err?.message || 'Failed to load results');
        }).finally(() => {
            if (!cancelled) setResultsLoading(false);
        });

        return () => { cancelled = true; };
    }, [deploymentId, surveyId]);

    // Live re-pull driven by the new-response signal / Refresh button in
    // SurveyExplorer (refreshNonce bumps). Re-fetches the session list and the
    // results rows in place WITHOUT clearing the user's session selection or
    // expansion state (unlike the mount effect above, which resets on a new
    // deployment). refreshNonce starts at 0 and the mount effect already did the
    // first load, so the `> 0` guard avoids a duplicate initial fetch.
    useEffect(() => {
        if (!deploymentId || refreshNonce === 0) return undefined;
        let cancelled = false;

        fetchExplorerSessions(surveyId, deploymentId).then(rows => {
            if (cancelled) return;
            setSessions(Array.isArray(rows) ? rows : []);
            setSessionsAt(Date.now());
        }).catch(() => { /* keep prior sessions on a transient refresh error */ });

        fetchExplorerResults(deploymentId).then(rows => {
            if (cancelled) return;
            setResultsRows(Array.isArray(rows) ? rows : []);
            setResultsAt(Date.now());
        }).catch(() => { /* keep prior results on a transient refresh error */ });

        return () => { cancelled = true; };
    }, [refreshNonce]); // eslint-disable-line react-hooks/exhaustive-deps

    // Build a deployment-wide conversation index once per take, independent of
    // the search query. This keeps a content search from issuing a request per
    // keystroke while still covering conversations that have not been selected.
    useEffect(() => {
        let cancelled = false;
        const sessionIds = sessions.map(s => String(s?.session_id || '')).filter(Boolean);
        if (!deploymentId || sessionIds.length === 0) {
            setConversationSearchTextBySession({});
            return () => { cancelled = true; };
        }
        const prefix = `${deploymentId}:`;
        const indexed = {};
        const pending = [];
        for (const sessionId of sessionIds) {
            const cached = conversationSearchCacheRef.current.get(`${prefix}${sessionId}`);
            if (cached != null) indexed[sessionId] = cached;
            else pending.push(sessionId);
        }
        setConversationSearchTextBySession(indexed);
        const workerCount = Math.min(4, pending.length);
        let cursor = 0;
        const loadOne = async () => {
            while (!cancelled) {
                const index = cursor;
                cursor += 1;
                if (index >= pending.length) return;
                const sessionId = pending[index];
                try {
                    const data = await fetchExplorerExchanges(sessionId);
                    const text = searchText(data?.exchanges || []) + ' ' + searchText(data?.prompts || {});
                    conversationSearchCacheRef.current.set(`${prefix}${sessionId}`, text);
                    if (!cancelled) setConversationSearchTextBySession(prev => ({ ...prev, [sessionId]: text }));
                } catch (_) {
                    // A failed take remains searchable through all other loaded
                    // response sources; retry occurs on a later refresh.
                }
            }
        };
        Promise.all(Array.from({ length: workerCount }, loadOne));
        return () => { cancelled = true; };
    }, [deploymentId, sessions]);

    useEffect(() => {
        const sessionIds = Array.from(selectedSessionIds);
        let cancelled = false;
        if (sessionIds.length === 0) {
            // Already-empty stays as it is. `exchangesBySessionId` is a
            // dependency of this effect, so handing it a fresh {} on every pass
            // re-triggers the effect, which hands it another fresh {} — an
            // endless render loop for as long as nothing is selected.
            setExchangesBySessionId(prev => (Object.keys(prev).length === 0 ? prev : {}));
            setPromptsByExchange(prev => (Object.keys(prev).length === 0 ? prev : {}));
            setNapByExchange(prev => (Object.keys(prev).length === 0 ? prev : {}));
            return () => { cancelled = true; };
        }

        const missing = sessionIds.filter(id => !exchangesBySessionId[id]);
        if (missing.length === 0) return () => { cancelled = true; };

        Promise.all(missing.map(async (sid) => {
            const data = await fetchExplorerExchanges(sid);
            return [sid, data];
        })).then(entries => {
            if (cancelled) return;
            setExchangesBySessionId(prev => {
                const next = { ...prev };
                for (const [sid, data] of entries) next[sid] = data || { exchanges: [], prompts: {} };
                return next;
            });
            setExpandedSessionIds(prev => {
                const next = new Set(prev);
                sessionIds.forEach(id => next.add(id));
                return next;
            });
            const allPrompts = {};
            const allNap = {};
            for (const [, data] of entries) {
                const { prompts, nap } = buildPromptsAndNap(data?.exchanges || []);
                Object.assign(allPrompts, data?.prompts || {}, prompts);
                Object.assign(allNap, nap);
            }
            setPromptsByExchange(prev => ({ ...prev, ...allPrompts }));
            setNapByExchange(prev => ({ ...prev, ...allNap }));
            setExchangesAt(Date.now());
        }).catch(err => {
            if (!cancelled) console.error('[ExploreTab] exchanges failed:', err);
        });

        return () => { cancelled = true; };
    }, [selectedSessionIds, exchangesBySessionId]);

    // Anonymous/browser takes write no recipient-keyed `results` rows, so the
    // deployment-wide fetch returns nothing for them. For each selected session
    // that has no matching `results` row, fall back to its session-keyed
    // `responses` and adapt them into the same shape the Results table renders.
    // Gated on results having finished loading so "has no results" is accurate.
    useEffect(() => {
        const ids = Array.from(selectedSessionIds);
        let cancelled = false;
        if (ids.length === 0 || resultsLoading) return () => { cancelled = true; };

        const hasRecipientResults = (session) => (resultsRows || []).some(r => {
            if (r.session_id && r.session_id === session.session_id) return true;
            if (r.recipient_id) {
                return session.participant_id === r.recipient_id
                    || session.responder_id === r.recipient_id
                    || session.recipient_id === r.recipient_id;
            }
            return false;
        });

        const needing = ids.filter(id => {
            if (sessionResponsesBySessionId[id] !== undefined) return false; // already fetched
            const session = sessions.find(s => s.session_id === id);
            if (!session) return false;
            return !hasRecipientResults(session);
        });
        if (needing.length === 0) return () => { cancelled = true; };

        Promise.all(needing.map(async (id) => {
            try {
                const rows = await fetchSessionResponses(id);
                return [id, adaptSessionResponsesForResults(rows)];
            } catch (err) {
                console.error('[ExploreTab] session responses failed:', err);
                return [id, []];
            }
        })).then(entries => {
            if (cancelled) return;
            setSessionResponsesBySessionId(prev => {
                const next = { ...prev };
                for (const [id, rows] of entries) next[id] = rows;
                return next;
            });
        });

        return () => { cancelled = true; };
    }, [selectedSessionIds, resultsRows, resultsLoading, sessions, sessionResponsesBySessionId]);

    // Fetch the survey descriptor structure (questions[]) for the audio bridge
    // and the Question column. Cleared when the survey changes.
    useEffect(() => {
        let cancelled = false;
        setSurveyStructure(null);
        if (!surveyId) return () => { cancelled = true; };
        fetchSurveyStructure(surveyId, deploymentId).then(struct => {
            if (!cancelled) setSurveyStructure(struct || null);
        }).catch(err => {
            if (!cancelled) console.error('[ExploreTab] survey structure failed:', err);
        });
        return () => { cancelled = true; };
    }, [surveyId, deploymentId]);

    // q.id (descriptor node id == kernel question_id) → variable_name. The
    // bridge that lets us map a response's question_id back to a results
    // row's variable_name (§1.1).
    const questionIdToVariable = useMemo(() => {
        const map = {};
        for (const q of surveyStructure?.questions || []) {
            if (q && q.id != null && q.variable_name) map[q.id] = q.variable_name;
        }
        return map;
    }, [surveyStructure]);

    // Question text keyed by BOTH variable_name (named result rows) AND node id
    // (anonymous adapted rows carry the node id in variable_name) — the lookup
    // must resolve either (§1.2).
    const textByKey = useMemo(() => {
        const map = {};
        for (const q of surveyStructure?.questions || []) {
            if (!q) continue;
            const text = q.text || '';
            if (q.variable_name) map[q.variable_name] = text;
            if (q.id != null && q.id !== '') map[String(q.id)] = text;
        }
        return map;
    }, [surveyStructure]);

    // The author's own number for each question ("Q2"), keyed the same two ways
    // as textByKey. An exchange records the question by its kernel id
    // (q_1785842391033), which is not a name the author has ever seen — this is
    // the bridge back to the number they wrote in the Designer.
    const questionIdentityByKey = useMemo(
        () => buildQuestionIdentity(surveyStructure?.questions || []),
        [surveyStructure],
    );

    // Recordings are keyed by variable name while timeline exchanges are keyed
    // by descriptor question id. One bridge, shared by the timeline table and
    // its export, so a "Has recording" cell can never disagree with the play
    // button beside the same answer. Legacy exchanges that already carry a
    // variable name resolve through the second pass.
    const audioByQuestionForSession = useCallback((sid) => {
        const audioByQuestion = {};
        const sessionAudio = { ...(sessionAudioBySessionId[sid] || {}) };
        // Anonymous/browser sessions fetched for the Results fallback carry the
        // raw path directly. Fold it into the shared variable-keyed bridge.
        for (const response of sessionResponsesBySessionId[sid] || []) {
            if (!response?.audio_storage_path) continue;
            const variable = questionIdToVariable[response.variable_name] || response.variable_name;
            if (variable) sessionAudio[variable] = response.audio_storage_path;
        }
        Object.entries(questionIdToVariable).forEach(([questionId, variable]) => {
            if (sessionAudio[variable]) audioByQuestion[questionId] = sessionAudio[variable];
        });
        Object.entries(sessionAudio).forEach(([variable, path]) => { audioByQuestion[variable] = path; });
        return audioByQuestion;
    }, [sessionAudioBySessionId, sessionResponsesBySessionId, questionIdToVariable]);

    // WP2 audio bridge: for each selected session that HAS a recipient-keyed
    // result (the ones the anonymous fallback effect SKIPS), fetch its RAW
    // responses and record audio_storage_path keyed by the resolved variable
    // name. Named-recipient results rows carry no audio; this attaches it.
    // Gated on the descriptor map being populated.
    useEffect(() => {
        const ids = Array.from(selectedSessionIds);
        let cancelled = false;
        if (ids.length === 0 || resultsLoading) return () => { cancelled = true; };
        if (Object.keys(questionIdToVariable).length === 0) return () => { cancelled = true; };

        const hasRecipientResults = (session) => (resultsRows || []).some(r => {
            if (r.session_id && r.session_id === session.session_id) return true;
            if (r.recipient_id) {
                return session.participant_id === r.recipient_id
                    || session.responder_id === r.recipient_id
                    || session.recipient_id === r.recipient_id;
            }
            return false;
        });

        const needing = ids.filter(id => {
            if (sessionAudioBySessionId[id] !== undefined) return false; // already fetched
            const session = sessions.find(s => s.session_id === id);
            if (!session) return false;
            return hasRecipientResults(session);
        });
        if (needing.length === 0) return () => { cancelled = true; };

        Promise.all(needing.map(async (id) => {
            try {
                const raw = await fetchSessionResponses(id);
                const map = {};
                for (const resp of (Array.isArray(raw) ? raw : [])) {
                    if (!resp?.audio_storage_path) continue;
                    const variable = questionIdToVariable[resp.question_id];
                    if (variable) map[variable] = resp.audio_storage_path;
                }
                return [id, map];
            } catch (err) {
                console.error('[ExploreTab] session audio bridge failed:', err);
                return [id, {}];
            }
        })).then(entries => {
            if (cancelled) return;
            setSessionAudioBySessionId(prev => {
                const next = { ...prev };
                for (const [id, map] of entries) next[id] = map;
                return next;
            });
        });

        return () => { cancelled = true; };
    }, [selectedSessionIds, resultsRows, resultsLoading, sessions, sessionAudioBySessionId, questionIdToVariable]);

    useEffect(() => {
        if (!dragging) return undefined;
        const handleMove = (e) => {
            if (!containerRef.current) return;
            const rect = containerRef.current.getBoundingClientRect();
            const next = (e.clientY - rect.top) / Math.max(1, rect.height);
            setSplitRatio(Math.min(0.82, Math.max(0.18, next)));
        };
        const handleUp = () => setDragging(false);
        window.addEventListener('mousemove', handleMove);
        window.addEventListener('mouseup', handleUp);
        return () => {
            window.removeEventListener('mousemove', handleMove);
            window.removeEventListener('mouseup', handleUp);
        };
    }, [dragging]);

    const participantEmailMap = useMemo(() => {
        const map = new Map();
        for (const s of sessions || []) {
            if (s.participant_id && s.email) map.set(s.participant_id, s.email);
            if (s.responder_id && s.email) map.set(s.responder_id, s.email);
            if (s.recipient_id && s.email) map.set(s.recipient_id, s.email);
        }
        for (const row of resultsRows || []) {
            if (row.recipient_id && row.email) map.set(row.recipient_id, row.email);
        }
        return map;
    }, [sessions, resultsRows]);

    // Single source of truth for a session's display identity (DEF-C5): the
    // recipient email/display name when known, else a synthesized anonymous
    // handle from the session timestamp. Shared by the sessions list AND the
    // matrix rows so the two surfaces can never diverge.
    const recipientHandle = useCallback((session) => {
        if (!session) return '-';
        const email = session.email
            || participantEmailMap.get(session.participant_id)
            || participantEmailMap.get(session.responder_id)
            || participantEmailMap.get(session.recipient_id)
            || '';
        const isAnon = !email || email === '-';
        return isAnon ? synthesizeAnonymousHandle(session) : (session.recipient_display || email);
    }, [participantEmailMap]);

    const debouncedSessionSearch = useDebouncedValue(sessionSearch);

    // The Sessions/Matrix search covers the entire recorded response space
    // before a take is selected. deploymentResponses is session-keyed and is
    // the primary source; resultsRows fills gaps for legacy/partial records.
    const responseSearchTextBySession = useMemo(() => {
        const bySession = new Map();
        for (const row of deploymentResponses || []) {
            if (!row?.session_id) continue;
            const sessionId = String(row.session_id);
            bySession.set(sessionId, `${bySession.get(sessionId) || ''} ${searchText(row)}`);
        }
        return bySession;
    }, [deploymentResponses]);
    const resultSearchTextBySession = useMemo(() => {
        const bySessionId = new Map();
        const byRecipientId = new Map();
        for (const row of resultsRows || []) {
            const text = searchText(row);
            if (row?.session_id) {
                const sessionId = String(row.session_id);
                bySessionId.set(sessionId, `${bySessionId.get(sessionId) || ''} ${text}`);
            }
            if (row?.recipient_id) {
                const recipientId = String(row.recipient_id);
                byRecipientId.set(recipientId, `${byRecipientId.get(recipientId) || ''} ${text}`);
            }
        }
        const bySession = new Map();
        for (const session of sessions || []) {
            const sessionId = String(session?.session_id || '');
            if (!sessionId) continue;
            const associated = [
                bySessionId.get(sessionId),
                byRecipientId.get(String(session.participant_id || '')),
                byRecipientId.get(String(session.responder_id || '')),
                byRecipientId.get(String(session.recipient_id || '')),
            ].filter(Boolean).join(' ');
            if (associated) bySession.set(sessionId, associated);
        }
        return bySession;
    }, [resultsRows, sessions]);

    const filteredSessions = useMemo(() => {
        const lower = debouncedSessionSearch.trim().toLowerCase();
        if (!lower) return sessions;
        return sessions.filter(s => {
            const fields = [
                s.email,
                s.recipient_display,
                s.participant_id,
                s.responder_id,
                s.recipient_id,
                s.session_id,
                s.completion_status,
                responseSearchTextBySession.get(String(s.session_id || '')),
                resultSearchTextBySession.get(String(s.session_id || '')),
                conversationSearchTextBySession[String(s.session_id || '')],
            ];
            return fields.some(v => String(v || '').toLowerCase().includes(lower));
        });
    }, [conversationSearchTextBySession, debouncedSessionSearch, resultSearchTextBySession, responseSearchTextBySession, sessions]);

    const aiContentMatchSessionIds = useMemo(() => {
        const lower = debouncedSessionSearch.trim().toLowerCase();
        if (!lower) return new Set();
        return new Set(sessions
            .filter(s => String(conversationSearchTextBySession[String(s.session_id || '')] || '').toLowerCase().includes(lower))
            .map(s => s.session_id));
    }, [conversationSearchTextBySession, debouncedSessionSearch, sessions]);

    // The Sessions and Matrix presentations share one filtered, paged session
    // collection. Selection is keyed by session id, so it survives paging and
    // opening either pane full-screen.
    const pageCount = Math.max(1, Math.ceil(filteredSessions.length / sessionsPageSize));
    const pageStart = sessionsPage * sessionsPageSize;
    const pagedSessions = useMemo(
        () => filteredSessions.slice(pageStart, pageStart + sessionsPageSize),
        [filteredSessions, pageStart, sessionsPageSize],
    );
    // An Analysis surface linked to the recipient behind an answer. Reveal that
    // session: clear any search hiding it, select it alone so the lower pane
    // shows their responses, and page to where the row actually is.
    const [revealSessionId, setRevealSessionId] = useState(null);
    useEffect(() => {
        const sessionId = openSession?.sessionId;
        if (!sessionId || !sessions.length) return;
        const match = sessions.find(s => String(s.session_id) === String(sessionId));
        if (!match) return;
        // The author may have left Explore on the matrix; the selected session
        // is only visible in the sessions list.
        setTopView('sessions');
        setSessionSearch('');
        setSelectedSessionIds(new Set([match.session_id]));
        setRevealSessionId(match.session_id);
        onSessionOpened?.();
    }, [openSession, sessions, onSessionOpened]);

    // Paging is settled separately, because clearing the search above debounces
    // and then resets the page to 0. Holding the target until the search has
    // actually settled stops that reset from undoing the page we just chose.
    useEffect(() => {
        if (!revealSessionId || debouncedSessionSearch !== '') return;
        const position = filteredSessions.findIndex(s => String(s.session_id) === String(revealSessionId));
        if (position < 0) return;
        setSessionsPage(Math.floor(position / sessionsPageSize));
        setRevealSessionId(null);
    }, [revealSessionId, debouncedSessionSearch, filteredSessions, sessionsPageSize]);

    // Clamp the page when the filter/count shrinks; reset to the first page when
    // the search text changes.
    useEffect(() => { setSessionsPage(p => Math.min(p, pageCount - 1)); }, [pageCount]);
    useEffect(() => { setSessionsPage(0); }, [debouncedSessionSearch]);

    const matrixColumns = useMemo(() => {
        const cols = [];
        for (const q of surveyStructure?.questions || []) {
            if (q?.variable_name) cols.push({ name: q.variable_name, label: q.variable_name, system: false });
        }
        cols.push({ name: '__completion_time', label: 'Completion time', system: true });
        cols.push({ name: '__recipient_group', label: 'Recipient group', system: true });
        cols.push({ name: '__status', label: 'Status', system: true });
        return cols;
    }, [surveyStructure]);

    const matrixRows = useMemo(() => {
        return (pagedSessions || []).map(s => {
            const ts = s.started_at || s.ts_start || s.ts || s.created_at;
            return {
                key: String(s.session_id || ''),
                handle: recipientHandle(s),
                timestamp: ts ? new Date(ts).toLocaleString() : '',
                status: s.completion_status || s.status || '',
            };
        });
    }, [pagedSessions, recipientHandle]);

    const matrixCells = useMemo(() => buildMatrixCells({
        sessions: filteredSessions,
        columns: matrixColumns,
        deploymentResponses,
        resultsRows,
    }), [filteredSessions, matrixColumns, deploymentResponses, resultsRows]);

    // --- Per-view CSV export ------------------------------------------------
    // Each button exports what its view is SHOWING. Plain browser downloads,
    // not the folder-picker flow, so they work in every browser and never ask
    // for a directory. Audio and uploads are indicated by a column, never
    // written out — a path in a spreadsheet implies a file openable from it.
    const csvName = useCallback((view) => buildFilename(surveyName || 'survey', deploymentId, `sra-${view}`, 'csv'),
        [surveyName, deploymentId]);

    const exportTopView = useCallback(() => {
        if (topView === 'matrix') {
            const { rows, columns } = buildMatrixExport(matrixRows, matrixColumns, matrixCells);
            exportRowsCsv(rows, columns, csvName('matrix'));
            return;
        }
        exportRowsCsv(buildSessionRows(filteredSessions, recipientHandle), SESSION_COLUMNS, csvName('sessions'));
    }, [topView, matrixRows, matrixColumns, matrixCells, filteredSessions, recipientHandle, csvName]);

    // The two session-level views, written into the same export folder as the
    // response-level one. Same scope rules as their toolbar buttons: sessions
    // is every match, matrix is the grid as displayed.
    const exportTopViewsToFolder = useCallback(async (folder) => {
        const target = folder || await ensureSubdir(await pickDirectory(), buildFolderName(surveyName));
        await writeFileToDir(target, 'sessions.csv', '\ufeff' + rowsToCsv(
            buildSessionRows(filteredSessions, recipientHandle), SESSION_COLUMNS));
        const matrix = buildMatrixExport(matrixRows, matrixColumns, matrixCells);
        await writeFileToDir(target, 'matrix.csv', '\ufeff' + rowsToCsv(matrix.rows, matrix.columns));
    }, [surveyName, filteredSessions, recipientHandle, matrixRows, matrixColumns, matrixCells]);

    const filteredResultsRows = useMemo(() => {
        if (selectedSessionIds.size === 0) return [];
        const recipientKeyed = (resultsRows || []).filter(r => {
            if (r.session_id && selectedSessionIds.has(r.session_id)) return true;
            if (r.recipient_id) {
                return sessions.some(s => selectedSessionIds.has(s.session_id) && (s.participant_id === r.recipient_id || s.responder_id === r.recipient_id || s.recipient_id === r.recipient_id));
            }
            return false;
        });
        // Merge in the session-keyed `responses` fallback for anonymous takes.
        // These are only fetched for selected sessions that had no recipient-keyed
        // row, so there's no overlap with recipientKeyed. Adapted rows carry
        // session_id, so grouping (byRecipient) keys them by session as intended.
        // DEF-C1: the fallback rows carry variable_name ← raw question_id
        // (q_… / Q000…). Remap to the real variable name via questionIdToVariable
        // so the Results VARIABLE column shows `userName`, not `Q00000002`; rows
        // whose name isn't in the map (already a real name) pass through unchanged.
        const fallback = [];
        for (const id of selectedSessionIds) {
            const adapted = sessionResponsesBySessionId[id];
            if (!Array.isArray(adapted) || !adapted.length) continue;
            for (const r of adapted) {
                const mapped = questionIdToVariable[r.variable_name];
                fallback.push(mapped ? { ...r, variable_name: mapped } : r);
            }
        }
        return recipientKeyed.concat(fallback);
    }, [resultsRows, selectedSessionIds, sessions, sessionResponsesBySessionId, questionIdToVariable]);

    // What the timeline export needs beyond the exchange rows themselves: the
    // prompts and payload the pane holds by sequence, the question wording, and
    // the recording bridge. Shared by both timeline exports so the standalone
    // CSV and the one inside the export folder carry the same content.
    const timelineExportHelpers = useCallback((dataBySession) => {
        const audioBySession = {};
        return {
            promptsFor: (sessionId, seq) => {
                const data = dataBySession[sessionId];
                const { prompts } = buildPromptsAndNap(data?.exchanges || []);
                // Session-scoped only: the pane's by-sequence maps are shared
                // across selected sessions, so reading them here would attach
                // one take's prompts to another's exchange.
                return prompts[seq] || data?.prompts?.[seq] || null;
            },
            napFor: (sessionId, seq) => {
                const data = dataBySession[sessionId];
                const { nap } = buildPromptsAndNap(data?.exchanges || []);
                return nap[seq] || null;
            },
            questionTextFor: (questionId) => (questionId ? (textByKey[questionId] || '') : ''),
            // The file names each question the way the screen does — the
            // author's number and variable, with the kernel id kept in its own
            // column for support work.
            questionIdentityFor: (questionId) => (questionId ? (questionIdentityByKey[questionId] || null) : null),
            audioFor: (sessionId, row) => {
                if (!audioBySession[sessionId]) audioBySession[sessionId] = audioByQuestionForSession(sessionId);
                const key = row?.question_id ? String(row.question_id) : '';
                return key ? audioBySession[sessionId][key] || null : null;
            },
        };
    }, [textByKey, questionIdentityByKey, audioByQuestionForSession]);

    const exportBottomView = useCallback(() => {
        const sessionById = new Map(sessions.map(s => [s.session_id, s]));
        const handleForSession = (sessionId) => recipientHandle(sessionById.get(sessionId) || {});
        if (resultsMode === 'timeline') {
            const rows = buildTimelineRows(
                Array.from(selectedSessionIds),
                exchangesBySessionId,
                handleForSession,
                timelineExportHelpers(exchangesBySessionId),
            );
            exportRowsCsv(rows, TIMELINE_COLUMNS, csvName('timeline'));
            return;
        }
        // An answer row from the deployment-wide results feed names its
        // recipient, not its take. Resolve the session it belongs to so the
        // Session column is filled and the recording bridge — which is keyed by
        // session — can be read, exactly as the Results table does on screen.
        const sessionIdFor = (r) => {
            if (r.session_id) return r.session_id;
            const match = sessions.find(s => selectedSessionIds.has(s.session_id)
                && (s.participant_id === r.recipient_id || s.responder_id === r.recipient_id || s.recipient_id === r.recipient_id));
            return match?.session_id || '';
        };
        const rows = buildResultRows(filteredResultsRows, {
            recipientFor: (r) => handleForSession(sessionIdFor(r)) || r.email || participantEmailMap.get(r.recipient_id) || '',
            questionFor: (r) => textByKey[String(r.variable_name)] ?? '',
            sessionFor: sessionIdFor,
            audioFor: (r) => sessionAudioBySessionId[sessionIdFor(r)]?.[r.variable_name] || null,
        });
        exportRowsCsv(rows, RESULT_COLUMNS, csvName('results'));
    }, [resultsMode, sessions, recipientHandle, selectedSessionIds, exchangesBySessionId, timelineExportHelpers,
        filteredResultsRows, participantEmailMap, textByKey, sessionAudioBySessionId, csvName]);

    const timelineHits = useMemo(() => {
        const query = timelineSearch.trim().toLowerCase();
        if (!query) return [];
        const hits = [];
        for (const sessionId of selectedSessionIds) {
            const data = exchangesBySessionId[sessionId];
            if (!data) continue;
            const { prompts: derivedPrompts, nap: derivedNap } = buildPromptsAndNap(data.exchanges || []);
            for (const row of data.exchanges || []) {
                const seq = row.exchange_seq;
                const humanText = searchText(row.human || row.raw_transcript || row.human_text || row.response_text || '');
                if (humanText.toLowerCase().includes(query)) hits.push({ sessionId, exchangeSeq: seq, source: 'human', label: `Exchange ${seq} · Recipient reply` });
                const aiText = searchText(row.ai_responses || row.ai_response || row.sam_response || row.ai || '');
                if (aiText.toLowerCase().includes(query)) hits.push({ sessionId, exchangeSeq: seq, source: 'ai', label: `Exchange ${seq} · AI reply` });
                const prompts = data.prompts?.[seq] || derivedPrompts[seq] || [];
                prompts.forEach((prompt, promptIndex) => {
                    if (searchText(prompt).toLowerCase().includes(query)) hits.push({ sessionId, exchangeSeq: seq, source: 'prompt', prompt, promptIndex, label: `Exchange ${seq} · Prompt` });
                });
                const nap = derivedNap[seq] || napByExchange[seq];
                if (nap && searchText(nap).toLowerCase().includes(query)) hits.push({ sessionId, exchangeSeq: seq, source: 'nap', nap, label: `Exchange ${seq} · Normalized AI Payload` });
            }
        }
        return hits;
    }, [exchangesBySessionId, napByExchange, selectedSessionIds, timelineSearch]);

    // --- Export (plan §2) --------------------------------------------------
    // Plain-text of a Value cell, mirroring RecipientResultsTable.renderValue but
    // (a) never truncated and (b) using the ACTUAL fetched upload byte length for
    // the size (WP6) instead of a metadata round-trip.
    const valueToPlainText = useCallback((valueJson, uploadGcs, fileMeta, uploadSizes) => {
        if (valueJson == null) return '';
        if (uploadGcs) {
            const filename = fileMeta[uploadGcs]?.filename || decodeURIComponent(uploadGcs.split('/').pop() || 'file');
            const bytes = uploadSizes.get(uploadGcs)?.bytes;
            const sizeStr = Number.isFinite(bytes) ? humanizeBytes(bytes) : '';
            return `${filename}${sizeStr ? ` - ${sizeStr}` : ''}`;
        }
        if (typeof valueJson === 'string') return mapNonAnswer(valueJson);
        if (typeof valueJson === 'number' || typeof valueJson === 'boolean') return String(valueJson);
        return JSON.stringify(valueJson);
    }, []);

    const onExportProgress = useCallback((p) => {
        if (!p) return;
        if (p.phase === 'files') showToast(`Exporting files ${p.done}/${p.total}`);
        else if (p.phase === 'audio' && p.ratio === undefined) showToast(`Transcoding audio ${p.done}/${p.total}`);
    }, []);

    // Results view export (§2.3 Results + §2.4 files + §2.5 audio). Assembles one
    // record per visible row, mirroring renderResults' grouping/handle/audio
    // enrichment so the CSV matches what's on screen, then writes the folder.
    const exportResultsView = useCallback(async () => {
        const byRecipient = {};
        // Chronological before grouping, so each recipient's rows come out in
        // the same order the Results table shows them by default. Grouping
        // preserves this order within each recipient.
        for (const r of sortByGathered(filteredResultsRows)) {
            const key = r.recipient_id || r.session_id || 'unknown';
            if (!byRecipient[key]) byRecipient[key] = { email: r.email || participantEmailMap.get(r.recipient_id) || '-', rows: [] };
            byRecipient[key].rows.push(r);
        }
        const recipientSessions = {};
        for (const s of sessions) {
            if (selectedSessionIds.has(s.session_id)) {
                const key = s.participant_id || s.responder_id || s.recipient_id || s.session_id;
                recipientSessions[key] = s;
            }
        }

        // Upload filenames come from file_uploads metadata; sizes come from the
        // actual fetch (below), never from this metadata.
        const fileMeta = {};
        await Promise.all(Array.from(selectedSessionIds).map(async (id) => {
            try {
                const files = await getFileUploads(id);
                for (const f of (files || [])) if (f.gcs_path) fileMeta[f.gcs_path] = f;
            } catch (_) { /* best-effort */ }
        }));

        const records = [];
        const clips = [];
        const uploads = [];
        for (const [recipientId, group] of Object.entries(byRecipient)) {
            const sess = recipientSessions[recipientId] || sessions.find(s => selectedSessionIds.has(s.session_id) && group.rows.some(r => r.session_id === s.session_id));
            const sessionTs = sess ? new Date(sess.started_at || sess.ts_start || sess.created_at).toLocaleString() : (group.rows[0]?.created_at ? new Date(group.rows[0].created_at).toLocaleString() : '');
            const isAnonymous = !group.rows.some(r => r.recipient_id) && (!group.email || group.email === '-');
            const handle = isAnonymous ? synthesizeAnonymousHandle(sess || {}) : (sess?.recipient_display || group.email);
            const audioForSession = sessionAudioBySessionId[sess?.session_id] || {};
            for (const r of group.rows) {
                const variable = String(r.variable_name || '');
                const questionText = textByKey[variable] ?? '';
                const uploadGcs = extractGcsPath(r.value_json);
                const audioGcs = r.audio_storage_path || audioForSession[r.variable_name] || null;
                if (uploadGcs) uploads.push({ gcsPath: uploadGcs, filename: fileMeta[uploadGcs]?.filename });
                if (audioGcs) clips.push({ gcsPath: audioGcs, handle, timestamp: r.created_at, questionText });
                records.push({ variable, questionText, valueJson: r.value_json, confidence: r.confidence, uploadGcs, audioGcs, handle, sessionTs, createdAt: r.created_at });
            }
        }

        // One shared path map each — the CSV cells and the written files read from
        // these, so they can never disagree (§2.2, §2.5).
        const audioPlan = planAudioClips(clips, surveyName);
        const uploadPlan = planUploads(uploads);

        const dir = await pickDirectory();
        const folder = await ensureSubdir(dir, buildFolderName(surveyName));

        let uploadSizes = new Map();
        if (uploadPlan.ordered.length) {
            const filesDir = await ensureSubdir(folder, 'files');
            uploadSizes = await exportUploads(filesDir, uploadPlan.ordered, { onProgress: onExportProgress });
        }
        let audioDurations = new Map();
        if (audioPlan.ordered.length) {
            const audioDir = await ensureSubdir(folder, 'audio');
            audioDurations = await exportAudioClips(audioDir, audioPlan.ordered, { onProgress: onExportProgress });
        }

        const columns = [
            { header: 'Variable', get: (r) => r.variable },
            { header: 'Question', get: (r) => r.questionText },
            { header: 'Value', get: (r) => valueToPlainText(r.valueJson, r.uploadGcs, fileMeta, uploadSizes) },
            { header: 'Confidence', get: (r) => (r.confidence == null || r.confidence === '' || !Number.isFinite(Number(r.confidence)) ? '' : `${Math.round(Number(r.confidence) * 100)}%`) },
            { header: 'Voice', get: (r) => (r.audioGcs ? (audioPlan.byGcs.get(r.audioGcs) || '') : '') },
            { header: 'Upload', get: (r) => (r.uploadGcs ? (uploadPlan.byGcs.get(r.uploadGcs) || '') : '') },
            { header: 'Created', get: (r) => (r.createdAt ? new Date(r.createdAt).toLocaleString() : '') },
            { header: 'Recipient', get: (r) => r.handle },
            { header: 'Session Timestamp', get: (r) => r.sessionTs },
        ];
        await writeFileToDir(folder, 'results.csv', '﻿' + rowsToCsv(records, columns));

        if (audioPlan.ordered.length) {
            const entries = audioPlan.ordered.map((clip) => ({
                path: clip.relPath,
                durationSeconds: audioDurations.get(clip.gcsPath)?.durationSeconds ?? null,
                title: `${clip.handle} — ${clip.questionText || ''}`.trim(),
            }));
            await writeFileToDir(folder, 'playlist.m3u', buildM3U(entries));
        }
        return folder;
    }, [filteredResultsRows, participantEmailMap, sessions, selectedSessionIds, sessionAudioBySessionId, textByKey, surveyName, valueToPlainText, onExportProgress]);

    // Timeline view export (§2.3 Timeline). No attachments — the timeline columns
    // reference none — so it writes only timeline.csv into the folder.
    const exportTimelineView = useCallback(async () => {
        const orderedSessions = Array.from(selectedSessionIds)
            .map(sid => sessions.find(s => s.session_id === sid))
            .filter(Boolean)
            .sort((a, b) => new Date(a.started_at || a.ts_start || a.created_at || 0) - new Date(b.started_at || b.ts_start || b.created_at || 0));

        // Make sure exchanges are loaded for every selected session (they may not
        // be if the user exports immediately after selecting).
        const dataBySid = {};
        await Promise.all(orderedSessions.map(async (s) => {
            const sid = s.session_id;
            let data = exchangesBySessionId[sid];
            if (!data) {
                try { data = await fetchExplorerExchanges(sid); } catch (_) { data = { exchanges: [], prompts: {} }; }
            }
            dataBySid[sid] = data || { exchanges: [], prompts: {} };
        }));

        // Same builder and columns as the toolbar's timeline CSV — the file in
        // the export folder and the standalone download are the same record.
        const sessionById = new Map(sessions.map(s => [s.session_id, s]));
        const rows = buildTimelineRows(
            orderedSessions.map(s => s.session_id),
            dataBySid,
            (sid) => recipientHandle(sessionById.get(sid) || {}),
            timelineExportHelpers(dataBySid),
        );

        const dir = await pickDirectory();
        const folder = await ensureSubdir(dir, buildFolderName(surveyName));
        await writeFileToDir(folder, 'timeline.csv', '﻿' + rowsToCsv(rows, TIMELINE_COLUMNS));
        return folder;
    }, [selectedSessionIds, sessions, exchangesBySessionId, surveyName, recipientHandle, timelineExportHelpers]);

    const handleExport = useCallback(async () => {
        if (selectedSessionIds.size === 0) {
            showToast('Select one or more sessions to export', 'error');
            return;
        }
        if (!directoryPickerSupported()) {
            showToast('Export needs Chrome or Edge (folder access is unavailable in this browser)', 'error');
            return;
        }
        try {
            showToast('Preparing export...');
            // The response-level export creates the folder and returns it, so
            // the session-level views land beside it rather than prompting for
            // a second directory.
            const folder = resultsMode === 'results' ? await exportResultsView() : await exportTimelineView();
            await exportTopViewsToFolder(folder);
            showToast('Export complete');
        } catch (err) {
            // User cancelling the directory picker rejects with AbortError — silent.
            if (err && (err.name === 'AbortError' || /abort/i.test(String(err.message)))) return;
            console.error('[ExploreTab] export failed:', err);
            showToast('Export failed', 'error');
        }
    }, [selectedSessionIds, resultsMode, exportResultsView, exportTimelineView, exportTopViewsToFolder]);

    // Register the export handler so the Tools ▾ "Export" entry (mounted up in
    // SurveyExplorer's command bar) can invoke it. Cleared on unmount so a stale
    // handler never fires from another tab.
    useEffect(() => {
        if (!exportHandlerRef) return undefined;
        exportHandlerRef.current = handleExport;
        return () => {
            if (exportHandlerRef.current === handleExport) exportHandlerRef.current = null;
        };
    }, [exportHandlerRef, handleExport]);

    const renderSessionsTable = () => {
        if (sessionsError) return <div style={{ color: '#F44336' }}>Error: {sessionsError}</div>;
        if (!sessions || sessions.length === 0) {
            // When the lifecycle explains the empty list (deployed-not-sent,
            // staged, or sent-and-waiting), show that reason — "<no results>"
            // alone reads as missing data.
            if (lifeMsg) return <div style={styles.emptyLifecycle}>{lifeMsg}</div>;
            return <div style={{ color: '#5F6368' }}>&lt;no results&gt;</div>;
        }

        const rowHeight = 32;
        const allPageSelected = pagedSessions.length > 0
            && pagedSessions.every(s => selectedSessionIds.has(s.session_id));
        const togglePage = (checked) => setSelectedSessionIds(prev => {
            const next = new Set(prev);
            pagedSessions.forEach(s => {
                if (checked) next.add(s.session_id);
                else next.delete(s.session_id);
            });
            return next;
        });
        return (
            <table style={styles.table}>
                <thead>
                    <tr>
                        <th style={{ ...styles.th, width: 24 }}>
                            <input type="checkbox" aria-label={`Select ${pagedSessions.length} sessions on this page`} checked={allPageSelected} onChange={(e) => togglePage(e.target.checked)} />
                        </th>
                        <th style={styles.th}>Timestamp</th>
                        <th style={styles.th}>Recipient</th>
                        <th style={styles.th}>Status</th>
                        <th style={styles.th}>Session</th>
                    </tr>
                </thead>
                <tbody>
                    {pagedSessions.map(s => {
                        const ts = s.started_at || s.ts_start || s.ts || s.created_at;
                        const t = ts ? new Date(ts).toLocaleString() : '';
                        const recipient = recipientHandle(s);
                        const status = s.completion_status || s.status || (s.completed_at || s.ts_end ? 'completed' : 'active');
                        const sid = String(s.session_id || '');
                        const sel = selectedSessionIds.has(s.session_id);
                        const aiContentMatch = aiContentMatchSessionIds.has(s.session_id);
                        return (
                            <tr key={sid} onClick={() => setSelectedSessionIds(prev => { const next = new Set(prev); next.has(s.session_id) ? next.delete(s.session_id) : next.add(s.session_id); return next; })} style={{ background: sel ? '#E3F2FD' : undefined, cursor: 'pointer', height: rowHeight }}>
                                <td style={styles.td}><input type="checkbox" aria-label={`Select ${recipient}`} checked={sel} onChange={(e) => { e.stopPropagation(); setSelectedSessionIds(prev => { const next = new Set(prev); e.target.checked ? next.add(s.session_id) : next.delete(s.session_id); return next; }); }} /></td>
                                <td style={styles.td}>{t}</td>
                                <td style={{ ...styles.td, fontWeight: 500 }}>
                                    {recipient}
                                    {aiContentMatch && <span style={styles.aiContentMatch}>AI content match</span>}
                                </td>
                                <td style={styles.td}>{status}</td>
                                <td title={sid} onClick={(e) => { e.stopPropagation(); navigator.clipboard?.writeText(sid); }} style={{ ...styles.td, fontFamily: styles.mono, fontSize: 12, color: '#5F6368', cursor: 'copy' }}>{sid.slice(0, 8)}</td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        );
    };

    const renderResults = () => {
        if (!deploymentId) return <div style={{ color: '#5F6368' }}>&lt;no deployment selected&gt;</div>;
        if (resultsError) return <div style={{ color: '#F44336' }}>Error: {resultsError}</div>;
        if (resultsLoading) return <div style={{ color: '#5F6368' }}>Loading results...</div>;
        if (selectedSessionIds.size === 0) return <div style={{ padding: '8px 0', color: '#5F6368' }}>Select sessions above to view results</div>;
        if (filteredResultsRows.length === 0) return <div style={{ padding: '8px 0', color: '#E65100' }}>No response data found for the selected session{selectedSessionIds.size > 1 ? 's' : ''}.</div>;

        const byRecipient = {};
        for (const r of filteredResultsRows) {
            const key = r.recipient_id || r.session_id || 'unknown';
            if (!byRecipient[key]) byRecipient[key] = { email: r.email || participantEmailMap.get(r.recipient_id) || '-', rows: [] };
            byRecipient[key].rows.push(r);
        }
        const recipientSessions = {};
        for (const s of sessions) {
            if (selectedSessionIds.has(s.session_id)) {
                const key = s.participant_id || s.responder_id || s.recipient_id || s.session_id;
                recipientSessions[key] = s;
            }
        }

        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {Object.entries(byRecipient).map(([recipientId, group]) => {
                    const sess = recipientSessions[recipientId] || sessions.find(s => selectedSessionIds.has(s.session_id) && group.rows.some(r => r.session_id === s.session_id));
                    const ts = sess ? new Date(sess.started_at || sess.ts_start || sess.created_at).toLocaleString() : (group.rows[0]?.created_at ? new Date(group.rows[0].created_at).toLocaleString() : '');
                    const shortSid = sess ? String(sess.session_id).slice(0, 8) : '';
                    const isOpen = expandedResultRecipients.has(recipientId);
                    // Recipient identity (§1.4): named → handle (recipient_display,
                    // else email); never the raw id. Anonymous take (no recipient,
                    // no email) → synthesized handle from the session timestamp.
                    const isAnonymous = !group.rows.some(r => r.recipient_id) && (!group.email || group.email === '-');
                    const handle = sess
                        ? recipientHandle(sess)
                        : isAnonymous
                            ? synthesizeAnonymousHandle({})
                            : group.email;
                    // Attach audio to named rows (WP2). Never overwrites an
                    // existing value (anonymous rows already carry their audio).
                    const audioForSession = sessionAudioBySessionId[sess?.session_id] || {};
                    const enrichedRows = group.rows.map(r => ({
                        ...r,
                        audio_storage_path: r.audio_storage_path || audioForSession[r.variable_name],
                    }));
                    return (
                        <div key={recipientId} style={styles.card}>
                            <div onClick={() => setExpandedResultRecipients(prev => { const next = new Set(prev); next.has(recipientId) ? next.delete(recipientId) : next.add(recipientId); return next; })} style={{ ...styles.cardHeader, borderBottom: isOpen ? '1px solid #E8EAED' : 'none' }}>
                                <span style={{ width: 16, fontSize: 12 }}>{isOpen ? 'v' : '>'}</span>
                                <span style={{ fontWeight: 600, flex: 1 }}>{handle}</span>
                                <span style={styles.muted}>{ts}</span>
                                {shortSid && <span style={{ ...styles.muted, fontFamily: styles.mono }}>sess:{shortSid}</span>}
                                <span style={{ color: '#94a3b8', fontSize: 12 }}>{group.rows.length} var{group.rows.length !== 1 ? 's' : ''}</span>
                            </div>
                            {isOpen && <div style={{ padding: 12 }}><RecipientResultsTable rows={enrichedRows} sessionId={sess?.session_id} textByKey={textByKey} /></div>}
                        </div>
                    );
                })}
            </div>
        );
    };

    const renderTimeline = () => {
        if (selectedSessionIds.size === 0) return <div style={{ color: '#5F6368' }}>Select sessions above to view timeline</div>;
        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {Array.from(selectedSessionIds)
                    .map(sid => sessions.find(s => s.session_id === sid))
                    .filter(Boolean)
                    .sort((a, b) => new Date(a.started_at || a.ts_start || a.created_at || 0) - new Date(b.started_at || b.ts_start || b.created_at || 0))
                    .map(session => {
                        const sid = session.session_id;
                        const isExpanded = expandedSessionIds.has(sid);
                        const email = session.email || participantEmailMap.get(session.participant_id) || participantEmailMap.get(session.responder_id) || '-';
                        const timestamp = (session.started_at || session.ts_start) ? new Date(session.started_at || session.ts_start).toLocaleString() : '';
                        const sessionData = exchangesBySessionId[sid];
                        const sessionExchanges = sessionData?.exchanges || [];
                        const audioByQuestion = audioByQuestionForSession(sid);
                        return (
                            <div key={sid} style={styles.card}>
                                <div onClick={() => setExpandedSessionIds(prev => { const next = new Set(prev); next.has(sid) ? next.delete(sid) : next.add(sid); return next; })} style={{ ...styles.cardHeader, borderBottom: isExpanded ? '1px solid #E8EAED' : 'none' }}>
                                    <span style={{ width: 16, fontSize: 12 }}>{isExpanded ? 'v' : '>'}</span>
                                    <span style={{ fontWeight: 600, flex: 1 }}>{email}</span>
                                    <span style={styles.muted}>{timestamp}</span>
                                    <span style={{ ...styles.muted, fontFamily: styles.mono }}>{String(sid).slice(0, 8)}</span>
                                </div>
                                {isExpanded && (
                                    <div style={{ padding: 12 }}>
                                        {sessionExchanges.length === 0 ? <div style={{ color: '#5F6368' }}>&lt;no exchanges&gt;</div> : (
                                            <TimelineExchangeTable
                                                sid={sid}
                                                exchanges={sessionExchanges}
                                                promptsByExchange={promptsByExchange}
                                                sessionPrompts={sessionData?.prompts}
                                                napByExchange={napByExchange}
                                                textByKey={textByKey}
                                                identityByKey={questionIdentityByKey}
                                                audioByQuestion={audioByQuestion}
                                                helpers={{ latestAiMeta, summarizeHuman, summarizeSam, humanizeBytes }}
                                                searchQuery={timelineSearch}
                                                focusHit={timelineSearchFocus?.sessionId === sid ? timelineSearchFocus : null}
                                                onOpenNap={(seq, nap) => setNapModal({ exchange_seq: seq, nap, showJson: false })}
                                                onOpenPrompt={(seq, prompt) => setPromptModal({ exchange_seq: seq, prompt, wrap: true })}
                                            />
                                        )}
                                        <SessionFileUploads sessionId={sid} />
                                    </div>
                                )}
                            </div>
                        );
                    })}
            </div>
        );
    };

    // Lifecycle sentence for the info bar and the empty-session explanation —
    // one source (survey_status + is_open) so it can never disagree with the
    // header badge/counters. Empty when the deployment is closed (history) or
    // the live numbers already tell the story.
    const lifeMsg = lifecycleMessage(enrichedDeployment);

    // Bottom-pane header (DEF-C8): "N recipients · <mode subtitle>". N is the
    // count of selected takes; a single selection shows its recipient handle.
    const selectedCount = selectedSessionIds.size;
    const bottomHeaderTitle = selectedCount === 0
        ? 'Responses'
        : selectedCount === 1
            ? recipientHandle(sessions.find(s => selectedSessionIds.has(s.session_id)))
            : `${selectedCount} recipients`;
    const bottomHeaderSub = selectedCount === 0
        ? ''
        : resultsMode === 'timeline'
            ? 'Conversation timeline · prompts and Normalized AI Payloads (NAP) are text and structured content exchanged with AI'
            : 'Response profiles · what each recipient answered';

    // --- Matrix view data (WP-C2) -----------------------------------------
    // Columns: authored questions from the survey structure + a few system
    // vars. Cells are sourced from the SAME resultsRows the Results view shows,
    // joined on the same recipient key, so matrix cells match Results by
    // construction. Memoized so MatrixView's column-visibility state is stable.
    const changeSessionsPageSize = (value) => {
        setSessionsPageSize(Number(value));
        setSessionsPage(0);
    };
    const openFullscreen = (pane) => {
        const listBody = pane === 'top' ? sessionsListBodyRef.current : resultsListBodyRef.current;
        paneScrollPositionsRef.current[pane] = listBody?.scrollTop || 0;
        setFullscreenPane(pane);
    };
    const closeFullscreen = (pane) => {
        setFullscreenPane(null);
        requestAnimationFrame(() => {
            const listBody = pane === 'top' ? sessionsListBodyRef.current : resultsListBodyRef.current;
            if (listBody) listBody.scrollTop = paneScrollPositionsRef.current[pane] || 0;
        });
    };
    const sessionRange = filteredSessions.length
        ? `${pageStart + 1}\u2013${Math.min(pageStart + sessionsPageSize, filteredSessions.length)} of ${filteredSessions.length} sessions`
        : '0 of 0 sessions';

    /**
     * The Sessions panel's view group. It says WHICH content the panel shows, so
     * it reads left beside the title; the commands that act on that content stay
     * right in the command group below.
     */
    const renderTopViewControls = () => (
        <Segmented
            options={[{ key: 'sessions', label: 'Sessions' }, { key: 'matrix', label: 'Matrix' }]}
            value={topView}
            onChange={setTopView}
        />
    );

    /**
     * The Sessions panel's header command group (§3: icon → title → count pill →
     * view group → commands right). Title and count are supplied by <Panel>
     * itself; this is only the command group.
     */
    const renderTopCommands = (inOverlay = false) => (
        <>
            <button type="button" onClick={exportTopView} title={topView === 'matrix' ? 'Download the matrix as shown (CSV)' : 'Download every matching session (CSV)'} aria-label={topView === 'matrix' ? 'Export matrix to CSV' : 'Export sessions to CSV'} style={styles.pagerBtn}>Export</button>
            <input aria-label="Search all session content" type="text" placeholder="Search sessions" value={sessionSearch} onChange={e => setSessionSearch(e.target.value)} style={styles.search} />
            {/* Selection commands live in the panel head with the other buttons —
                commands are always grouped, never split between head and footer.
                Sessions carries Clear selection only; deleting responses is not
                offered from this view. */}
            <button
                type="button"
                disabled={selectedSessionIds.size === 0}
                onClick={() => setSelectedSessionIds(new Set())}
                style={styles.pagerBtn}
            >
                {selectedSessionIds.size > 0 ? `Clear selection (${selectedSessionIds.size})` : 'Clear selection'}
            </button>
            {!inOverlay && (
                <button ref={topFullscreenButtonRef} type="button" onClick={() => openFullscreen('top')} aria-label="Open sessions full screen" title="Open full screen" style={styles.iconButton}>⛶</button>
            )}
        </>
    );

    /** Selection banners that sit above the list body, inside the panel. */
    const renderTopControls = () => (
        <>
            {pagedSessions.length > 0
                && pagedSessions.every(s => selectedSessionIds.has(s.session_id))
                && filteredSessions.some(s => !selectedSessionIds.has(s.session_id)) && (
                    <div style={styles.selectAllBanner}>
                        <span>{pagedSessions.length} sessions on this page selected.</span>
                        <button type="button" onClick={() => setSelectedSessionIds(prev => new Set([...prev, ...filteredSessions.map(s => s.session_id)]))} style={styles.linkButton}>
                            Select all {filteredSessions.length} matching sessions
                        </button>
                    </div>
                )}
        </>
    );

    const renderTopList = () => (topView === 'matrix' ? (
        <MatrixView
            rows={matrixRows}
            columns={matrixColumns}
            cells={matrixCells}
            selected={selectedSessionIds}
            onToggleSelect={(id) => setSelectedSessionIds(prev => { const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next; })}
            onOpenInAnalysis={(name) => onOpenInAnalysis?.(name)}
        />
    ) : renderSessionsTable());

    /**
     * Pagination bar per list-management.md: Previous, page/total status, Next,
     * then the page-size selector. The status states page and total in plain
     * language ("Page 2 of 6 · 143 total"); the range is the permitted extra.
     */
    const renderTopPager = () => (
        <div style={styles.pager} aria-label="Sessions pagination">
            <button type="button" style={styles.pagerBtn} disabled={sessionsPage <= 0} onClick={() => setSessionsPage(p => Math.max(0, p - 1))}>Previous</button>
            <span>
                {`Page ${Math.min(sessionsPage + 1, Math.max(pageCount, 1))} of ${Math.max(pageCount, 1)} · ${filteredSessions.length} total`}
                <span style={styles.muted}>{` · ${sessionRange}`}</span>
            </span>
            <button type="button" style={styles.pagerBtn} disabled={sessionsPage >= pageCount - 1} onClick={() => setSessionsPage(p => Math.min(pageCount - 1, p + 1))}>Next</button>
            <label style={styles.pageSizeLabel}>Rows
                <select aria-label="Sessions per page" value={sessionsPageSize} onChange={e => changeSessionsPageSize(e.target.value)} style={styles.pageSizeSelect}>
                    {PAGE_SIZE_OPTIONS.map(size => <option key={size} value={size}>{size}</option>)}
                </select>
            </label>
        </div>
    );

    const focusTimelineHit = (hit, openDetail = false) => {
        setExpandedSessionIds(prev => new Set(prev).add(hit.sessionId));
        setTimelineSearchFocus(hit);
        if (!openDetail) return;
        if (hit.source === 'prompt') setPromptModal({ exchange_seq: hit.exchangeSeq, prompt: hit.prompt, wrap: true });
        if (hit.source === 'nap') setNapModal({ exchange_seq: hit.exchangeSeq, nap: hit.nap, showJson: false });
    };

    const renderTimelineSearchHits = () => {
        if (resultsMode !== 'timeline' || !timelineSearch.trim()) return null;
        return (
            <div style={styles.timelineSearchHits} aria-live="polite">
                {timelineHits.length === 0 ? <span>No matching conversation content in loaded selected sessions.</span> : timelineHits.map((hit, index) => (
                    <div key={`${hit.sessionId}-${hit.exchangeSeq}-${hit.source}-${hit.promptIndex || index}`} style={styles.timelineSearchHit}>
                        <button type="button" onClick={() => focusTimelineHit(hit)} style={styles.linkButton}>{hit.label}</button>
                        {(hit.source === 'prompt' || hit.source === 'nap') && <button type="button" onClick={() => focusTimelineHit(hit, true)} style={styles.smallButton}>Open</button>}
                    </div>
                ))}
            </div>
        );
    };

    /**
     * The Responses panel's view group: which content is shown, and the sentence
     * that describes it. Left, beside the title. The subtitle truncates rather
     * than growing, so it can never push Export or the search box off the head —
     * the full sentence stays readable on hover.
     */
    const renderBottomViewControls = () => (
        <>
            <Segmented
                options={[{ key: 'results', label: 'Results' }, { key: 'timeline', label: 'Timeline' }]}
                value={resultsMode}
                onChange={setResultsMode}
            />
            {/* The title itself is the panel's — rendering it here too printed
                "Responses Responses" in the header. Only the subtitle stays. */}
            {bottomHeaderSub && <span style={styles.bottomSub} title={bottomHeaderSub}>{bottomHeaderSub}</span>}
        </>
    );

    /**
     * The Responses panel's header command group. <Panel> renders the title and
     * the view group; this is the command group only (§3: commands sit right in
     * the header).
     */
    const renderBottomToolbar = (inOverlay = false) => (
        <>
            {resultsMode === 'timeline' && <input aria-label="Search conversation, prompts, and AI payloads" type="text" placeholder="Search conversation, prompts, and AI payloads" value={timelineSearch} onChange={e => setTimelineSearch(e.target.value)} style={styles.search} />}
            <button
                type="button"
                onClick={exportBottomView}
                disabled={selectedSessionIds.size === 0}
                title={selectedSessionIds.size === 0
                    ? 'Select sessions above to export'
                    : (resultsMode === 'timeline' ? 'Download every exchange of the selected sessions (CSV)' : 'Download the selected sessions\u2019 answers (CSV)')}
                aria-label={resultsMode === 'timeline' ? 'Export timeline to CSV' : 'Export results to CSV'}
                style={{ ...styles.pagerBtn, marginLeft: 'auto' }}
            >Export</button>
            {!inOverlay && (
                <button ref={bottomFullscreenButtonRef} type="button" onClick={() => openFullscreen('bottom')} aria-label="Open responses full screen" title="Open full screen" style={styles.iconButton}>⛶</button>
            )}
        </>
    );

    const renderBottomContent = () => (resultsMode === 'results' ? renderResults() : <>{renderTimelineSearchHits()}{renderTimeline()}</>);

    return (
        <div style={styles.root}>
            <div aria-hidden={fullscreenPane ? 'true' : undefined} inert={fullscreenPane ? '' : undefined} style={styles.backgroundContent}>
            <div style={styles.overview}>
                <div style={{ fontWeight: 600 }}>{surveyName || 'Untitled Survey'}</div>
                <div style={styles.muted}>Survey: {surveyId || '<none>'}</div>
                <div style={styles.muted}>Deployment: {deploymentId || '<none>'}</div>
                {sessionsAt && <div style={styles.muted}>Sessions: {new Date(sessionsAt).toLocaleTimeString()}</div>}
                {exchangesAt && <div style={styles.muted}>Exchanges: {new Date(exchangesAt).toLocaleTimeString()}</div>}
            </div>

            {lifeMsg && <div style={styles.lifecycleBanner}>{lifeMsg}</div>}

            <div ref={containerRef} style={styles.panes}>
                <Panel
                    ref={sessionsListRef}
                    tabIndex={-1}
                    title="Sessions"
                    count={`${filteredSessions.length} matching`}
                    controls={renderTopViewControls()}
                    actions={renderTopCommands()}
                    style={{ flex: splitRatio, minHeight: 160 }}
                    bodyStyle={{ display: 'flex', flexDirection: 'column' }}
                >
                    {renderTopControls()}
                    <div ref={sessionsListBodyRef} style={styles.listBody}>{renderTopList()}</div>
                    {renderTopPager()}
                </Panel>

                <div role="separator" aria-orientation="horizontal" tabIndex={0} onMouseDown={() => setDragging(true)} onKeyDown={(e) => {
                    const step = 0.02;
                    if (e.key === 'ArrowUp' || e.key === 'PageUp') {
                        e.preventDefault();
                        setSplitRatio(r => Math.max(0.18, r - step));
                    } else if (e.key === 'ArrowDown' || e.key === 'PageDown') {
                        e.preventDefault();
                        setSplitRatio(r => Math.min(0.82, r + step));
                    }
                }} style={styles.splitter} aria-label="Resize sessions / timeline split">
                    <div style={styles.splitterGrip} />
                </div>

                <Panel
                    ref={resultsListRef}
                    tabIndex={-1}
                    title={bottomHeaderTitle}
                    controls={renderBottomViewControls()}
                    actions={renderBottomToolbar()}
                    style={{ flex: 1 - splitRatio, minHeight: 220 }}
                    bodyRef={resultsListBodyRef}
                    bodyStyle={styles.resultsListBody}
                >
                    {renderBottomContent()}
                </Panel>
            </div>
            </div>
            {fullscreenPane === 'top' && (
                <FullScreenListOverlay title="Sessions" onClose={() => closeFullscreen('top')} returnFocusRef={topFullscreenButtonRef} disableEscape={Boolean(promptModal) || Boolean(napModal)}>
                    {/* Same split as the panel head: view group left, commands right. */}
                    <div style={styles.toolbar}>{renderTopViewControls()}<div style={styles.toolbarCommands}>{renderTopCommands(true)}</div></div>
                    {renderTopControls()}
                    <div style={styles.listBody}>{renderTopList()}</div>
                    {renderTopPager()}
                </FullScreenListOverlay>
            )}
            {fullscreenPane === 'bottom' && (
                <FullScreenListOverlay title="Responses" onClose={() => closeFullscreen('bottom')} returnFocusRef={bottomFullscreenButtonRef} disableEscape={Boolean(promptModal) || Boolean(napModal)}>
                    <div style={styles.resultsToolbar}>{renderBottomViewControls()}<div style={styles.toolbarCommands}>{renderBottomToolbar(true)}</div></div>
                    <div style={styles.resultsListBody}>{renderBottomContent()}</div>
                </FullScreenListOverlay>
            )}
            <PromptModal promptModal={promptModal} setPromptModal={setPromptModal} />
            <NapModal napModal={napModal} setNapModal={setNapModal} />
        </div>
    );
}

const styles = {
    root: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minWidth: 0,
        overflow: 'hidden',
        padding: 12,
        background: '#fff',
    },
    backgroundContent: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
    },
    overview: {
        padding: 8,
        background: 'var(--color-bg-base)',
        border: '1px solid #E8EAED',
        borderRadius: 6,
        marginBottom: 8,
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        flexWrap: 'wrap',
    },
    // Lifecycle message — normal-size, readable text (not muted, not a
    // tooltip). Colors from the shared design tokens, no hex literals.
    lifecycleBanner: {
        padding: `${tokens.spaceSm}px ${tokens.spaceMd}px`,
        marginBottom: tokens.spaceSm,
        background: tokens.primaryFaint,
        border: `1px solid ${tokens.borderLight}`,
        borderRadius: tokens.radiusMd,
        color: tokens.textPrimary,
        fontSize: tokens.fontSizeMd,
        fontWeight: tokens.fontWeightMedium,
        lineHeight: 1.5,
    },
    emptyLifecycle: {
        color: tokens.textPrimary,
        fontSize: tokens.fontSizeMd,
        lineHeight: 1.5,
        padding: `${tokens.spaceSm}px 0`,
    },
    panes: {
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
    },
    pane: {
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        border: '1px solid #E8EAED',
        borderRadius: 6,
        background: '#fff',
        padding: 8,
    },
    listBody: {
        flex: '1 1 auto',
        minHeight: 0,
        overflow: 'auto',
    },
    // The lower pane owns a separate scroll container. Keep its toolbar as a
    // non-shrinking first flex child so it stays flush to the pane top while
    // results/timeline rows scroll only inside the body below it.
    resultsPane: {
        minHeight: 0,
    },
    resultsToolbar: {
        flex: '0 0 auto',
        position: 'relative',
        zIndex: 1,
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        minHeight: 30,
        marginBottom: 8,
        paddingBottom: 6,
        background: '#fff',
        borderBottom: '1px solid #E8EAED',
    },
    resultsListBody: {
        flex: '1 1 0',
        minHeight: 0,
        overflow: 'auto',
    },
    splitter: {
        height: 8,
        background: 'var(--color-neutral-200)',
        borderTop: '1px solid var(--color-border-medium)',
        borderBottom: '1px solid var(--color-border-medium)',
        cursor: 'row-resize',
        margin: 'var(--spacing-xs) 0',
        flex: '0 0 auto',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    splitterGrip: {
        width: 40,
        height: 3,
        background: 'var(--color-neutral-400)',
        borderRadius: 'var(--radius-sm)',
    },
    toolbar: {
        position: 'sticky',
        top: 0,
        zIndex: 20,
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        marginBottom: 8,
        minHeight: 30,
        background: '#fff',
        borderBottom: '1px solid #E8EAED',
        paddingBottom: 6,
    },
    // Full-screen toolbars mirror the panel head: view group left, commands
    // right, so the same control sits in the same place in both.
    toolbarCommands: {
        marginLeft: 'auto',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        flex: 'none',
    },
    search: {
        fontSize: 12,
        padding: '2px 6px',
        borderRadius: 4,
        border: '1px solid #cbd5e1',
        width: 160,
        marginLeft: 'auto',
    },
    listCount: {
        color: '#5F6368',
        fontSize: 12,
        whiteSpace: 'nowrap',
    },
    pageSizeLabel: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        fontSize: 12,
        color: '#5F6368',
        whiteSpace: 'nowrap',
    },
    pageSizeSelect: {
        border: '1px solid #cbd5e1',
        borderRadius: 4,
        background: '#fff',
        padding: '2px 4px',
    },
    iconButton: {
        width: 30,
        height: 30,
        padding: 0,
        border: '1px solid #DADCE0',
        background: '#fff',
        borderRadius: 4,
        cursor: 'pointer',
        fontSize: 17,
        lineHeight: 1,
    },
    selectAllBanner: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '6px 8px',
        marginBottom: 6,
        borderRadius: 4,
        background: '#EBF4FF',
        color: '#1E5A9D',
        fontSize: 12,
    },
    timelineSearchHits: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 6,
        alignItems: 'center',
        padding: '6px 0',
        color: '#5F6368',
        fontSize: 12,
    },
    timelineSearchHit: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        padding: '3px 6px',
        border: '1px solid #DADCE0',
        borderRadius: 4,
        background: '#F8F9FA',
    },
    linkButton: {
        padding: 0,
        color: '#1A73E8',
        background: 'transparent',
        border: 'none',
        fontSize: 12,
        fontWeight: 600,
        cursor: 'pointer',
        textDecoration: 'underline',
    },
    fullscreenBackdrop: {
        position: 'fixed',
        inset: 0,
        zIndex: MODAL_LAYERS.fullscreen,
        display: 'flex',
        alignItems: 'stretch',
        justifyContent: 'stretch',
        background: 'rgba(32, 33, 36, 0.58)',
    },
    fullscreenDialog: {
        display: 'flex',
        flexDirection: 'column',
        width: '100vw',
        height: '100vh',
        background: '#fff',
        padding: 16,
        boxSizing: 'border-box',
    },
    fullscreenHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flex: '0 0 auto',
        paddingBottom: 10,
        borderBottom: '1px solid #E8EAED',
    },
    fullscreenTitle: {
        margin: 0,
        color: '#202124',
        fontSize: 18,
    },
    fullscreenBody: {
        display: 'flex',
        flex: 1,
        minHeight: 0,
        flexDirection: 'column',
        overflow: 'hidden',
        paddingTop: 10,
    },
    table: {
        width: '100%',
        borderCollapse: 'collapse',
        fontSize: 13,
    },
    // Uppercased, muted column headers per the prototype (.srth) — shared by the
    // sessions list (DEF-C4) and the Results table (DEF-C9).
    th: {
        position: 'sticky',
        // 0, not 37: the controls row used to sit inside the scrolling body and
        // the header had to clear it. Those controls now live in the panel head
        // (§3), so the header sticks to the top of the body itself.
        top: 0,
        zIndex: 10,
        background: '#fff',
        textAlign: 'left',
        padding: '7px 8px',
        borderBottom: '1px solid #E8EAED',
        fontWeight: 600,
        fontSize: 11,
        color: '#888',
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
    },
    resultsTh: {
        // Results rows scroll in resultsListBody below the outer toolbar.
        // Its table header must therefore stick to that body's own top.
        top: 0,
    },
    td: {
        padding: '4px 6px',
        borderBottom: '1px solid #F5F6F7',
        verticalAlign: 'top',
    },
    colResizeHandle: {
        position: 'absolute',
        top: 0,
        right: -3,
        width: 6,
        height: '100%',
        cursor: 'col-resize',
        userSelect: 'none',
    },
    // The heading itself, as a button. Every font property is inherited from
    // the th so the header keeps its uppercase small-caps look — a bare
    // <button> would otherwise reset to the browser's control font.
    sortButton: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        padding: 0,
        border: 'none',
        background: 'none',
        cursor: 'pointer',
        font: 'inherit',
        color: 'inherit',
        letterSpacing: 'inherit',
        textTransform: 'inherit',
        textAlign: 'left',
    },
    sortArrows: {
        display: 'inline-flex',
        flexDirection: 'column',
        lineHeight: 0.75,
        fontSize: 7,
        // Nudged up to sit against the cap height of the uppercase label
        // rather than its baseline.
        marginTop: -1,
    },
    sortArrow: {
        display: 'block',
    },
    card: {
        border: '1px solid #E8EAED',
        borderRadius: 6,
        overflow: 'visible',
    },
    cardHeader: {
        background: 'var(--color-bg-base)',
        padding: '8px 12px',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
    },
    muted: {
        color: '#5F6368',
        fontSize: 12,
    },
    aiContentMatch: {
        display: 'inline-block',
        marginLeft: 6,
        padding: '1px 5px',
        borderRadius: 8,
        background: '#E8F0FE',
        color: '#1A73E8',
        fontSize: 10,
        fontWeight: 600,
        whiteSpace: 'nowrap',
    },
    subtle: {
        fontSize: 11,
        color: '#5F6368',
    },
    mono: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
    smallButton: {
        fontSize: 12,
        padding: '2px 8px',
        border: '1px solid #E8EAED',
        background: 'var(--color-bg-base)',
        borderRadius: 6,
        cursor: 'pointer',
    },
    warnButton: {
        fontSize: 12,
        padding: '2px 6px',
        border: '1px solid #E8EAED',
        background: '#FFF8E1',
        borderRadius: 6,
        cursor: 'pointer',
    },
    objectTag: {
        display: 'inline-flex',
        alignItems: 'center',
        padding: '2px 8px',
        borderRadius: 10,
        fontSize: 11,
        fontWeight: 600,
        cursor: 'pointer',
        border: 'none',
        background: '#EBF5FF',
        color: '#2563EB',
    },
    fileTag: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        padding: '2px 8px',
        border: '1px solid #E8EAED',
        background: 'var(--color-bg-base)',
        borderRadius: 8,
        cursor: 'pointer',
        fontSize: 12,
        color: '#3C4043',
    },
    fileName: {
        maxWidth: 140,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    filesBox: {
        marginTop: 8,
        padding: '8px 12px',
        background: '#FAFBFC',
        borderRadius: 6,
        border: '1px solid #E8EAED',
    },
    radioLabel: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
    },
    // Segmented control (prototype `.seg`) — DEF-C2 / DEF-C8.
    seg: {
        display: 'inline-flex',
        border: '1px solid #DADCE0',
        borderRadius: 5,
        overflow: 'hidden',
    },
    segBtn: {
        border: 'none',
        background: '#FFFFFF',
        padding: '5px 12px',
        fontSize: 12.5,
        fontWeight: 500,
        color: '#5F6368',
        cursor: 'pointer',
    },
    segBtnDivider: {
        borderRight: '1px solid #DADCE0',
    },
    segBtnOn: {
        background: '#EBF4FF',
        color: '#2B6BBF',
    },
    // "SURVEY SPACE" label left of the top toggle (DEF-C2).
    spaceLabel: {
        fontSize: 12,
        fontWeight: 600,
        color: '#888',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        marginRight: 2,
    },
    // "n selected" count beside the top toggle (DEF-C3).
    selCount: {
        fontSize: 12,
        color: '#888',
        marginLeft: 2,
    },
    // Bottom-pane header (DEF-C8).
    bottomTitle: {
        fontSize: 13.5,
        fontWeight: 600,
        color: '#202124',
    },
    // Truncates instead of growing: the head is a fixed-height row, and a
    // sentence that grows is a sentence that shoves the commands out of view.
    bottomSub: {
        fontSize: 12,
        color: '#888',
        minWidth: 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    // Sessions pager footer (DEF-C7).
    pager: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '7px 4px 2px',
        marginTop: 6,
        borderTop: '1px solid #E8EAED',
        fontSize: 11.5,
        color: '#888',
    },
    pagerBtn: {
        background: '#FFFFFF',
        color: '#3C4043',
        border: '1px solid #DADCE0',
        borderRadius: 4,
        padding: '3px 10px',
        fontSize: 12,
        fontWeight: 500,
        cursor: 'pointer',
    },
    // Confidence chip (DEF-C10) — tier colors come from CONF_TIER_STYLE.
    confChip: {
        display: 'inline-block',
        fontSize: 11,
        fontWeight: 600,
        padding: '1px 7px',
        borderRadius: 10,
    },
    // Voice play affordance (prototype `.play`) — DEF-C11.
    playPill: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        fontSize: 11.5,
        fontWeight: 500,
        color: '#2B6BBF',
        background: '#EBF4FF',
        border: '1px solid #D1E7FF',
        borderRadius: 12,
        padding: '2px 9px',
        cursor: 'pointer',
    },
    // Upload file chip (prototype `.filechip`) — DEF-C11.
    filechip: {
        display: 'inline-block',
        fontSize: 11.5,
        fontWeight: 500,
        color: '#2563EB',
        background: '#EBF5FF',
        border: 'none',
        borderRadius: 10,
        padding: '2px 8px',
        cursor: 'pointer',
    },
    modalBackdrop: {
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.35)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: MODAL_LAYERS.detail,
    },
    modal: {
        background: '#fff',
        padding: 16,
        minWidth: 520,
        maxWidth: 960,
        maxHeight: '80vh',
        overflow: 'auto',
        borderRadius: 6,
    },
    modalHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: 8,
    },
    pre: {
        whiteSpace: 'pre-wrap',
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
        fontSize: 12,
        lineHeight: 1.5,
        background: 'var(--color-bg-base)',
        padding: 12,
        border: '1px solid #E8EAED',
        borderRadius: 6,
    },
};
